import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useState, type ReactElement } from "react";
import { Button } from "../../shared/components/ui/button";
import { Input } from "../../shared/components/ui/input";
import { Label } from "../../shared/components/ui/label";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

/**
 * Step 1: Welcome and display name
 */
export function WelcomeStep(): ReactElement {
  const [displayName, setDisplayName] = useState("");
  const { transitionTo } = useTransitionNavigation();

  const handleContinue = (): void => {
    // Store display name in localStorage temporarily
    // Will be saved to backend in final step
    if (displayName.trim()) {
      localStorage.setItem("user-displayName", displayName.trim());
    }
    transitionTo("/user/preferences", pageTransitions.slideHorizontal);
  };

  const handleSkip = (): void => {
    localStorage.removeItem("user-displayName");
    transitionTo("/user/preferences", pageTransitions.slideHorizontal);
  };

  return (
    <motion.div
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">Welcome to Lumi! 👋</h1>
        <p className="text-lg text-neutral-400">Let's personalize your experience</p>
      </div>

      <div className="bg-black/50 backdrop-blur-md rounded-lg border border-neutral-800/60 p-8">
        <div className="space-y-6">
          <div className="grid gap-3">
            <Label htmlFor="displayName" className="text-base">
              What should we call you?
            </Label>
            <Input
              id="displayName"
              type="text"
              placeholder="Enter your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={50}
              autoFocus
            />
            <p className="text-xs text-neutral-500">This will appear on your profile. You can change it later.</p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleSkip} variant="outline" className="flex-1">
              Skip
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!displayName.trim()}
              className="flex-1 flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 mt-8">
        <div className="w-8 h-1 bg-lumi-accent rounded-full" />
        <div className="w-8 h-1 bg-neutral-800 rounded-full" />
        <div className="w-8 h-1 bg-neutral-800 rounded-full" />
      </div>
      <p className="text-center text-xs text-neutral-500 mt-3">Step 1 of 3</p>
    </motion.div>
  );
}
